import React, { ChangeEvent } from 'react';

import { HomeOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Col, Flex, Popconfirm, Row } from 'antd';

import { AddressSummaryCard } from './AddressSummaryCard';
import { DuplicateShipmentAddressForm } from './DuplicateShipmentAddressForm';
import { DuplicateShipmentCarrierForm } from './DuplicateShipmentCarrierForm';
import { EditableCard } from './EditableCard';
import { ReturnCarrierSummary } from './ReturnCarrierSummary';
import TextInput from '@/components/forms/TextInput';
import { ICreateShipmentAddress } from '@/models/address';

const COLOR_SUCCESS = '#52c41a';
const COLOR_PLACEHOLDER_BG = '#fafafa';
const COLOR_PLACEHOLDER_BORDER = '#d9d9d9';

interface ICarrierOption {
  carrierCode: string;
  label: string;
}

interface ISnapshotHandlers {
  onEditStart: () => void;
  onCancel: () => void;
}

interface IDuplicateShipmentReturnSectionProps {
  values: {
    returnCarrier: string;
    returnReference: string;
    returnCarrierTrackingCode: string;
    weightInGrams: string;
    addReturn: boolean;
    returnSenderEmail: string;
    returnRecipientEmail: string;
  };
  setFieldValue: (field: string, value: unknown) => void;
  initialHasReturn: boolean;
  returnLabelUrl: string | null;
  returnFile: File | null;
  setReturnFile: (f: File | null) => void;
  carrierOptions: ICarrierOption[];
  returnCarrierHandlers: ISnapshotHandlers;
  returnSenderHandlers: ISnapshotHandlers;
  returnRecipientHandlers: ISnapshotHandlers;
  handleEditingChange: (editing: boolean) => void;
  hasErrorsInFields: (fields: string[]) => boolean;
  getAddressFields: (prefix: string) => string[];
  getAddressFormProps: (prefix: string) => {
    prefix: string;
    values: Record<string, string>;
    errors: Record<string, string | undefined>;
    touched: Record<string, boolean | undefined>;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    setFieldValue: (field: string, value: string) => void;
    showPhone: boolean;
  };
  getAddressSummary: (prefix: string) => ICreateShipmentAddress;
  hasAddressError: (prefix: string) => boolean;
}

export const DuplicateShipmentReturnSection = ({
  values,
  setFieldValue,
  initialHasReturn,
  returnLabelUrl,
  returnFile,
  setReturnFile,
  carrierOptions,
  returnCarrierHandlers,
  returnSenderHandlers,
  returnRecipientHandlers,
  handleEditingChange,
  hasErrorsInFields,
  getAddressFields,
  getAddressFormProps,
  getAddressSummary,
  hasAddressError,
}: IDuplicateShipmentReturnSectionProps) => (
  <Card
    size="small"
    title={`Return Shipment${values.returnReference ? `: ${values.returnReference}` : ''}`}
    extra={
      values.addReturn && (
        <Popconfirm
          title="Remove return label"
          description="Are you sure you want to remove the return label?"
          onConfirm={() => setFieldValue('addReturn', false)}
          okButtonProps={{ danger: true }}
          okText="Yes"
          cancelText="No"
          placement="bottomRight"
          rootClassName="popconfirm-inline-buttons"
        >
          <Button
            data-testid="duplicate-remove-return-button"
            type="link"
            size="small"
            danger
          >
            Remove label
          </Button>
        </Popconfirm>
      )
    }
  >
    {!values.addReturn ? (
      <Flex
        justify="center"
        align="center"
        style={{
          padding: '2rem',
          background: COLOR_PLACEHOLDER_BG,
          borderRadius: '.375rem',
          border: `.0625rem dashed ${COLOR_PLACEHOLDER_BORDER}`,
        }}
      >
        <Button
          data-testid="duplicate-add-return-button"
          onClick={() => setFieldValue('addReturn', true)}
          style={{
            borderColor: COLOR_SUCCESS,
            color: COLOR_SUCCESS,
          }}
        >
          Add Label
        </Button>
      </Flex>
    ) : (
      <Flex vertical gap="0.75rem">
        <EditableCard
          title="Carrier"
          defaultEditing={!initialHasReturn}
          onEditingChange={handleEditingChange}
          onEditStart={returnCarrierHandlers.onEditStart}
          onCancel={returnCarrierHandlers.onCancel}
          summary={
            <ReturnCarrierSummary
              carrier={values.returnCarrier}
              weightInGrams={values.weightInGrams || ''}
              trackingCode={values.returnCarrierTrackingCode || ''}
              labelUrl={returnLabelUrl}
            />
          }
        >
          <DuplicateShipmentCarrierForm
            carrier={values.returnCarrier || ''}
            setCarrier={(c) => setFieldValue('returnCarrier', c)}
            file={returnFile}
            setFile={setReturnFile}
            weightInGrams={values.weightInGrams || ''}
            onWeightChange={(v) => setFieldValue('weightInGrams', v)}
            labelUrl={returnLabelUrl}
            carrierOptions={carrierOptions}
          />
          <TextInput
            label="Return name of trip:"
            name="returnReference"
            value={values.returnReference || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFieldValue('returnReference', e.target.value)
            }
          />
          <TextInput
            label="Carrier tracking code"
            name="returnCarrierTrackingCode"
            value={values.returnCarrierTrackingCode || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFieldValue('returnCarrierTrackingCode', e.target.value)
            }
          />
        </EditableCard>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} data-testid="address-section-return-sender">
            <EditableCard
              title="Sender"
              defaultEditing={hasAddressError('returnSender')}
              disableDone={hasErrorsInFields(getAddressFields('returnSender'))}
              onEditingChange={handleEditingChange}
              onEditStart={returnSenderHandlers.onEditStart}
              onCancel={returnSenderHandlers.onCancel}
              summary={
                <AddressSummaryCard
                  label="Sender"
                  icon={<UserOutlined />}
                  address={getAddressSummary('returnSender')}
                  email={values.returnSenderEmail}
                />
              }
            >
              <DuplicateShipmentAddressForm
                {...getAddressFormProps('returnSender')}
              />
            </EditableCard>
          </Col>
          <Col xs={24} md={12} data-testid="address-section-return-recipient">
            <EditableCard
              title="Recipient"
              defaultEditing={hasAddressError('returnRecipient')}
              disableDone={hasErrorsInFields(
                getAddressFields('returnRecipient')
              )}
              onEditingChange={handleEditingChange}
              onEditStart={returnRecipientHandlers.onEditStart}
              onCancel={returnRecipientHandlers.onCancel}
              summary={
                <AddressSummaryCard
                  label="Recipient"
                  icon={<HomeOutlined />}
                  address={getAddressSummary('returnRecipient')}
                  email={values.returnRecipientEmail}
                />
              }
            >
              <DuplicateShipmentAddressForm
                {...getAddressFormProps('returnRecipient')}
              />
            </EditableCard>
          </Col>
        </Row>
      </Flex>
    )}
  </Card>
);
